import {
  Building2,
  CalendarClock,
  ChevronRight,
  Eye,
  Landmark,
  Radio,
  Video,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { isLoggedin } from "../../lib/isLoggedin";
import { loginRedirectPath } from "../../utils/role.utils";

type VideoCardStatus =
  | "Live"
  | "Scheduled"
  | "Available"
  | "Archived"
  | "Processing";

export interface VideoStreamCard {
  id: number | string;
  caseNo: string;
  title: string;
  court: string;
  courtType: "High Court" | "District Court";
  status: VideoCardStatus;
  time?: string;
  date?: string;
  duration?: string;
  access?: "Granted" | "Request Required" | "Pending";
  viewers?: number;
  room?: string;
  watchCnr?: string;
  highCourt?: string;
  bench?: string;
  state?: string;
  district?: string;
  courtComplex?: string;
  courtEstablishment?: string;
}

interface VideoCardProps {
  rows?: VideoStreamCard[];
  watchType?: "stream" | "recording";
}

const SURFACES = [
  "linear-gradient(135deg, #0f172a 0%, #1d4ed8 52%, #14b8a6 100%)",
  "linear-gradient(135deg, #111827 0%, #be123c 54%, #f59e0b 100%)",
  "linear-gradient(135deg, #052e2b 0%, #047857 56%, #38bdf8 100%)",
  "linear-gradient(135deg, #1e1b4b 0%, #7c3aed 58%, #f97316 100%)",
];

const isLive = (stream: VideoStreamCard) => stream.status === "Live";

const streamTime = (time: string) =>
  time.replace(/^Started\s+/i, "").replace(/^Starts\s+/i, "");

const getMetaTime = (stream: VideoStreamCard) => {
  if (stream.time) return streamTime(stream.time);
  if (stream.duration) return stream.duration;
  return stream.date || "On demand";
};

const getCourtPath = (stream: VideoStreamCard) => {
  if (stream.courtType === "High Court") {
    return [stream.bench, stream.room].filter(Boolean).join(" / ");
  }

  return [
    stream.district,
    stream.courtComplex,
    stream.courtEstablishment,
    stream.room,
  ]
    .filter(Boolean)
    .join(" / ");
};

function StatusBadge({ status }: { status: VideoCardStatus }) {
  const statusClass =
    status === "Live"
      ? "bg-red-500 text-white shadow-sm shadow-red-950/20"
      : status === "Available"
        ? "bg-emerald-50 text-emerald-700"
        : status === "Processing"
          ? "bg-amber-50 text-amber-700"
          : "bg-white/90 text-slate-700";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${statusClass}`}
    >
      {status === "Live" ? (
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
      ) : (
        <CalendarClock size={12} />
      )}
      {status}
    </span>
  );
}

function StreamPoster({
  stream,
  index,
  onOpen,
}: {
  stream: VideoStreamCard;
  index: number;
  onOpen: () => void;
}) {
  const live = isLive(stream);
  const canOpen = live || stream.status === "Available";

  return (
    <button
      type="button"
      onClick={canOpen ? onOpen : undefined}
      disabled={!canOpen}
      className={`group/poster relative block aspect-video w-full overflow-hidden rounded-lg text-left outline-none ring-1 ring-slate-900/5 ${
        canOpen
          ? "cursor-pointer shadow-md shadow-slate-900/12 focus-visible:ring-2 focus-visible:ring-blue-500"
          : "cursor-not-allowed"
      }`}
      style={{ background: SURFACES[index % SURFACES.length] }}
    >
      <span className="absolute inset-0 bg-[linear-gradient(to_top,rgba(2,6,23,.86),rgba(15,23,42,.12)_58%,rgba(255,255,255,.12))]" />
      <span className="absolute inset-0 opacity-35 [background-image:linear-gradient(rgba(255,255,255,.18)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.14)_1px,transparent_1px)] [background-size:38px_38px]" />

      <div className="absolute left-3 top-3 flex items-center gap-2">
        <StatusBadge status={stream.status} />
        {stream.viewers !== undefined && live && (
          <span className="inline-flex items-center gap-1 rounded-md bg-black/45 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
            <Eye size={12} />
            {stream.viewers.toLocaleString("en-IN")}
          </span>
        )}
      </div>

      <div
        className={`absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full transition ${
          canOpen
            ? "bg-white text-blue-700 shadow-lg shadow-slate-950/25 group-hover/poster:scale-105"
            : "bg-white/18 text-white ring-1 ring-white/20 backdrop-blur"
        }`}
      >
        {canOpen ? <Video size={22} /> : <Radio size={22} />}
      </div>

      <div className="absolute inset-x-0 bottom-0 p-4 text-white">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase text-white/70">
              {stream.courtType}
            </p>
            <h3 className="mt-1 truncate text-xl font-black leading-tight">
              {stream.room || stream.court}
            </h3>
          </div>
          <span className="shrink-0 rounded-md bg-black/50 px-2.5 py-1 text-xs font-bold backdrop-blur">
            {getMetaTime(stream)}
          </span>
        </div>
      </div>
    </button>
  );
}

function EmptyStreams() {
  return (
    <div className="col-span-full rounded-lg border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
      <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
        <Video size={20} />
      </div>
      <p className="mt-3 text-sm font-semibold text-slate-700">
        No streams match.
      </p>
      <p className="mt-1 text-xs text-slate-500">
        Live and scheduled courtroom streams will appear here.
      </p>
    </div>
  );
}

export default function VideoCard({
  rows = [],
  watchType = "stream",
}: VideoCardProps) {
  const navigate = useNavigate();

  const openStream = (stream: VideoStreamCard) => {
    const params = new URLSearchParams({
      type: watchType,
      cnr: String(stream.watchCnr || stream.caseNo || stream.id),
    });

    if (stream.date) {
      params.set(
        watchType === "recording" ? "hearingDate" : "date",
        stream.date,
      );
    }

    const path = `/watch?${params.toString()}`;
    navigate(isLoggedin() ? path : loginRedirectPath(path));
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {rows.map((stream, index) => {
        const live = isLive(stream);
        const canOpen = live || stream.status === "Available";
        const courtPath = getCourtPath(stream);

        return (
          <article
            key={stream.id}
            className="group rounded-lg border border-slate-200 bg-white p-2 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg hover:shadow-slate-900/8"
          >
            <StreamPoster
              stream={stream}
              index={index}
              onOpen={() => openStream(stream)}
            />

            <div className="grid grid-cols-[40px_1fr_auto] gap-3 px-1.5 py-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  stream.courtType === "High Court"
                    ? "bg-blue-50 text-blue-700"
                    : "bg-emerald-50 text-emerald-700"
                }`}
              >
                {stream.courtType === "High Court" ? (
                  <Landmark size={18} />
                ) : (
                  <Building2 size={18} />
                )}
              </div>

              <div className="min-w-0">
                <button
                  type="button"
                  onClick={canOpen ? () => openStream(stream) : undefined}
                  disabled={!canOpen}
                  className={`line-clamp-2 text-left text-sm font-bold leading-5 ${
                    canOpen
                      ? "text-slate-950 hover:text-blue-700"
                      : "cursor-not-allowed text-slate-700"
                  }`}
                >
                  {stream.title}
                </button>
                <p className="mt-1 truncate text-xs font-medium text-slate-500">
                  {stream.court} · {stream.caseNo}
                </p>
                {courtPath && (
                  <p className="mt-1 truncate text-xs text-slate-400">
                    {courtPath}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={canOpen ? () => openStream(stream) : undefined}
                disabled={!canOpen}
                className={`mt-0.5 flex h-10 w-10 items-center justify-center rounded-lg transition ${
                  canOpen
                    ? "bg-blue-600 text-white shadow-sm shadow-blue-950/20 hover:bg-blue-700"
                    : "cursor-not-allowed bg-slate-100 text-slate-400"
                }`}
                aria-label={canOpen ? "Open video" : "Video unavailable"}
              >
                {canOpen ? <ChevronRight size={18} /> : <CalendarClock size={17} />}
              </button>
            </div>
          </article>
        );
      })}

      {rows.length === 0 && <EmptyStreams />}
    </div>
  );
}
